import { useEffect, useState } from 'react';
import { OperationResult } from 'urql';
import { Icon } from '@global-ecom/nitro-uds/elements';

import { Button } from 'ui/elements/Button';
import { useTranslate } from 'hooks/useTranslations';
import Modal from 'ui/components/Modal';
import { useProductQuery } from '__generated__/graphql';
import { extractError } from 'utils/extractError';
import { useFeature } from 'hooks/useFeature';
import { getSizesFromSizeGroups } from 'utils/products';

import { Sizes } from './Sizes';
import { Styles } from './Styles';
import { Price } from './Price';
import CloseButton from './CloseButton';

type UpdateInput = { size: string; swatch: string };

export type EditProductModalProps = {
  price: number;
  size: string;
  color: string;
  id: string;
  masterId: string;
  onClose: () => void;
  update: (input: UpdateInput) => Promise<OperationResult>;
};

export const EditProductModal: React.FC<EditProductModalProps> = props => {
  const t = useTranslate();
  const [selectedColor, setSelectedColor] = useState(props.color);
  const [selectedSize, setSelectedSize] = useState(props.size);
  const [isSameProduct, setIsSameProduct] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string>();
  const showPriceDifference = useFeature('SHOW_PRICE_DIFFERENCE');

  const [product] = useProductQuery({
    variables: { id: props.masterId },
    pause: !props.masterId,
  });

  const masterProduct = product.data?.product;

  const chosenVariant = masterProduct?.variations.find(
    x => x.colorValue === selectedColor
  );

  useEffect(() => {
    if (!chosenVariant) return;

    setIsSameProduct(chosenVariant.colorValue === props.color);
  }, [chosenVariant, props.color]);

  if (!chosenVariant || !masterProduct) {
    return null;
  }

  const sizes = getSizesFromSizeGroups(chosenVariant.sizeGroups);
  const selectedSizeIsOrderable = sizes.find(
    x => x.id === selectedSize
  )?.orderable;

  const priceDifference =
    (chosenVariant.salePrice || chosenVariant.price) - props.price;

  const productVariations = (masterProduct.variations || []).filter(
    x => !x.displayOutOfStock?.comingsoon
  );
  const styleNumberPrefix = t('styleNumberModalCart');
  const colorPrefix = t('colorModalCart');
  return (
    <Modal
      onClickOutside={props.onClose}
      variant="dark"
      className="w-full max-w-md p-8 sm:p-10 m-auto"
      dataTestId="edit-product-modal"
    >
      <CloseButton
        className="absolute top-5 right-5 !mt-0"
        onClick={props.onClose}
      />
      <div className="space-y-4 sm:space-y-6">
        <div className="space-y-2">
          {styleNumberPrefix ? (
            <>
              <span className="text-puma-black-300">{styleNumberPrefix}</span>
              <span className="text-puma-black">
                {props.masterId}_{selectedColor}
              </span>
            </>
          ) : (
            <span className="text-puma-black-300">
              #{props.masterId}_{selectedColor}
            </span>
          )}

          <h2 className="text-xl md:text-3xl font-bold leading-tight">
            {masterProduct.name}
          </h2>
          {colorPrefix ? (
            <h3>
              <span className="text-puma-black-300">{colorPrefix}</span>
              <span className="text-puma-black">{chosenVariant.colorName}</span>
            </h3>
          ) : (
            <h3 className="text-puma-black">{chosenVariant.colorName}</h3>
          )}
        </div>

        <Styles
          desiredSwatch={selectedColor}
          variations={productVariations}
          onChange={swatch => {
            setUpdating(false);
            setError(undefined);
            setSelectedColor(swatch);
          }}
        />
        <hr />
        <Sizes
          sizeGroups={chosenVariant.sizeGroups}
          desiredSize={selectedSize}
          onChange={newSize => {
            setUpdating(false);
            setError(undefined);
            setSelectedSize(newSize);
          }}
        />
        {error && (
          <p className="flex items-center space-x-3 justify-between text-puma-red border border-puma-red font-bold px-3 py-2 text-sm rounded mb-2 -mt-3">
            <span>{error}</span>
            <Icon name="exclamation-outline" size="base" />
          </p>
        )}
        {!selectedSizeIsOrderable && (
          <p className="flex items-center space-x-3 justify-between text-puma-red border border-puma-red font-bold px-3 py-2 text-sm rounded mb-2 -mt-3">
            <span>{t('selectedSizeIsNotOrderable')}</span>
            <Icon name="exclamation-outline" size="base" />
          </p>
        )}
        <Button
          label={t('updateItem')}
          id="save-product"
          height="tall"
          className="w-full"
          loading={updating}
          disabled={updating || !selectedSizeIsOrderable}
          onClick={async () => {
            setUpdating(true);
            const result = await props.update({
              size: selectedSize,
              swatch: selectedColor,
            });
            if (result.error) {
              setError(extractError(result));
              setUpdating(false);
            } else {
              props.onClose();
            }
          }}
        >
          {t('updateItem')}
          {showPriceDifference && !isSameProduct && (
            <PriceDifference difference={priceDifference} />
          )}
        </Button>
      </div>
    </Modal>
  );
};

const PriceDifference: React.FC<{ difference: number }> = props => {
  if (!props.difference) return null;

  return (
    <>
      {' ('}
      <Price amount={props.difference} showSign />
      {')'}
    </>
  );
};
